import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { Check, X, AlertTriangle, Plus, RefreshCw } from 'lucide-react';

interface TagRule {
  id: number;
  originalPattern: string;
  canonicalTag: string | null;
  status: 'active' | 'excluded' | 'review';
  matchType: 'exact' | 'prefix' | 'contains' | 'regex';
  notes: string | null;
}

interface TagRuleGroup {
  canonical: string;
  activeCount: number;
  excludedCount: number;
  reviewCount: number;
  patterns: TagRule[];
}

export const TagRulesManager: React.FC = () => {
  const [rules, setRules] = useState<TagRuleGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'active' | 'review' | 'excluded'>('active');
  const [newPattern, setNewPattern] = useState('');
  const [newCanonical, setNewCanonical] = useState('USPS');
  const [adding, setAdding] = useState(false);

  const fetchRules = async () => {
    try {
      setLoading(true);
      const response = await api.get('/shipper-tag-rules');
      const data = response.data;
      if (data.success) {
        setRules(data.data);
      } else {
        setError('Failed to fetch rules');
      }
    } catch (err) {
      setError('Error fetching rules');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRules();
  }, []);

  const updateRuleStatus = async (ruleId: number, status: 'active' | 'excluded' | 'review') => {
    try {
      const response = await api.patch(`/shipper-tag-rules/${ruleId}/status`, { status });
      if (response.data) {
        fetchRules();
      }
    } catch (err) {
      console.error('Error updating rule status', err);
    }
  };

  const addRule = async () => {
    if (!newPattern.trim()) return;
    setAdding(true);
    try {
      const response = await api.post('/shipper-tag-rules', {
        originalPattern: newPattern.toUpperCase(),
        canonicalTag: newCanonical || null,
        status: 'active',
        matchType: 'exact'
      });
      if (response.data) {
        setNewPattern('');
        fetchRules();
      }
    } catch (err) {
      console.error('Error adding rule', err);
    } finally {
      setAdding(false);
    }
  };

  const getFilteredRules = (): TagRule[] => {
    const allPatterns: TagRule[] = [];
    rules.forEach(group => {
      group.patterns.forEach(pattern => {
        if (activeTab === 'active' && pattern.status === 'active') {
          allPatterns.push(pattern);
        } else if (activeTab === 'review' && pattern.status === 'review') {
          allPatterns.push(pattern);
        } else if (activeTab === 'excluded' && pattern.status === 'excluded') {
          allPatterns.push(pattern);
        }
      });
    });
    return allPatterns;
  };

  const totalReview = rules.reduce((sum, g) => sum + g.reviewCount, 0);
  const totalExcluded = rules.reduce((sum, g) => sum + g.excludedCount, 0);
  const totalActive = rules.reduce((sum, g) => sum + g.activeCount, 0);

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <RefreshCw className="animate-spin" style={{ margin: '0 auto' }} />
        <p>Loading tag rules...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: '#b3412c' }}>
        <AlertTriangle style={{ margin: '0 auto 1rem' }} />
        <p>{error}</p>
      </div>
    );
  }

  const filteredRules = getFilteredRules();

  return (
    <div style={{ padding: '1.5rem' }}>
      <div style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '0.5rem' }}>
          🏷️ Shipper Tag Rules
        </h2>
        <p style={{ color: '#857f73', fontSize: '0.875rem' }}>
          Manage how shipper names are normalized to canonical tags (e.g., "US MAIL" → "USPS")
        </p>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', borderBottom: '1px solid #e2ddd1', paddingBottom: '0.5rem' }}>
        <button
          onClick={() => setActiveTab('active')}
          style={{
            padding: '0.5rem 1rem',
            borderRadius: '0.375rem',
            border: 'none',
            cursor: 'pointer',
            fontWeight: activeTab === 'active' ? 600 : 400,
            backgroundColor: activeTab === 'active' ? 'var(--color-primary)' : 'transparent',
            color: activeTab === 'active' ? 'white' : '#4a463f'
          }}
        >
          Active ({totalActive})
        </button>
        <button
          onClick={() => setActiveTab('review')}
          style={{
            padding: '0.5rem 1rem',
            borderRadius: '0.375rem',
            border: 'none',
            cursor: 'pointer',
            fontWeight: activeTab === 'review' ? 600 : 400,
            backgroundColor: activeTab === 'review' ? 'var(--color-warning)' : 'transparent',
            color: activeTab === 'review' ? 'white' : '#4a463f',
            display: 'flex',
            alignItems: 'center',
            gap: '0.25rem'
          }}
        >
          <AlertTriangle size={14} />
          Needs Review ({totalReview})
        </button>
        <button
          onClick={() => setActiveTab('excluded')}
          style={{
            padding: '0.5rem 1rem',
            borderRadius: '0.375rem',
            border: 'none',
            cursor: 'pointer',
            fontWeight: activeTab === 'excluded' ? 600 : 400,
            backgroundColor: activeTab === 'excluded' ? '#857f73' : 'transparent',
            color: activeTab === 'excluded' ? 'white' : '#4a463f'
          }}
        >
          Excluded ({totalExcluded})
        </button>
      </div>

      {/* Add new rule */}
      {activeTab === 'active' && (
        <div style={{ 
          display: 'flex', 
          gap: '0.5rem', 
          marginBottom: '1rem',
          padding: '1rem',
          backgroundColor: '#f5f2ea',
          borderRadius: '0.5rem',
          alignItems: 'center'
        }}>
          <input
            type="text"
            placeholder="New pattern (e.g., US POSTAL)"
            value={newPattern}
            onChange={(e) => setNewPattern(e.target.value)}
            style={{
              flex: 1,
              padding: '0.5rem 0.75rem',
              borderRadius: '0.375rem',
              border: '1px solid #d3ccbc',
              fontSize: '0.875rem'
            }}
          />
          <span style={{ color: '#857f73' }}>→</span>
          <select
            value={newCanonical}
            onChange={(e) => setNewCanonical(e.target.value)}
            style={{
              padding: '0.5rem 0.75rem',
              borderRadius: '0.375rem',
              border: '1px solid #d3ccbc',
              fontSize: '0.875rem'
            }}
          >
            <option value="USPS">USPS</option>
            <option value="UPS">UPS</option>
            <option value="FEDEX">FEDEX</option>
            <option value="AMAZON">AMAZON</option>
          </select>
          <button
            onClick={addRule}
            disabled={adding || !newPattern.trim()}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.25rem',
              padding: '0.5rem 1rem',
              borderRadius: '0.375rem',
              border: 'none',
              backgroundColor: 'var(--color-success)',
              color: 'white',
              cursor: adding || !newPattern.trim() ? 'not-allowed' : 'pointer',
              opacity: adding || !newPattern.trim() ? 0.5 : 1
            }}
          >
            <Plus size={16} />
            Add
          </button>
        </div>
      )}

      {/* Rules list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {filteredRules.length === 0 ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#857f73' }}>
            No rules in this category
          </div>
        ) : (
          filteredRules.map((rule) => (
            <div
              key={rule.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                padding: '0.75rem 1rem',
                backgroundColor: rule.status === 'review' ? '#efe3c8' : rule.status === 'excluded' ? '#efebe1' : 'white',
                border: '1px solid #e2ddd1',
                borderRadius: '0.5rem'
              }}
            >
              <div style={{ flex: 1 }}>
                <span style={{ fontWeight: 600, fontFamily: 'monospace' }}>{rule.originalPattern}</span>
                <span style={{ color: '#857f73', margin: '0 0.5rem' }}>→</span>
                <span style={{ 
                  backgroundColor: '#efebe1', 
                  color: 'var(--color-gray-900)', 
                  padding: '0.125rem 0.5rem', 
                  borderRadius: '0.25rem',
                  fontSize: '0.75rem',
                  fontWeight: 600
                }}>
                  {rule.canonicalTag || 'NONE'}
                </span>
                {rule.matchType !== 'exact' && (
                  <span style={{ 
                    marginLeft: '0.5rem',
                    fontSize: '0.75rem',
                    color: '#857f73'
                  }}>
                    ({rule.matchType})
                  </span>
                )}
              </div>

              {/* Action buttons */}
              <div style={{ display: 'flex', gap: '0.25rem' }}>
                {rule.status !== 'active' && (
                  <button
                    onClick={() => updateRuleStatus(rule.id, 'active')}
                    title="Include in stats"
                    style={{
                      padding: '0.375rem',
                      borderRadius: '0.25rem',
                      border: 'none',
                      backgroundColor: '#e2e7d5',
                      color: 'var(--color-success)',
                      cursor: 'pointer'
                    }}
                  >
                    <Check size={16} />
                  </button>
                )}
                {rule.status !== 'excluded' && (
                  <button
                    onClick={() => updateRuleStatus(rule.id, 'excluded')}
                    title="Exclude from stats"
                    style={{
                      padding: '0.375rem',
                      borderRadius: '0.25rem',
                      border: 'none',
                      backgroundColor: '#f1e2da',
                      color: '#b3412c',
                      cursor: 'pointer'
                    }}
                  >
                    <X size={16} />
                  </button>
                )}
                {rule.status !== 'review' && (
                  <button
                    onClick={() => updateRuleStatus(rule.id, 'review')}
                    title="Mark for review"
                    style={{
                      padding: '0.375rem',
                      borderRadius: '0.25rem',
                      border: 'none',
                      backgroundColor: '#efe3c8',
                      color: 'var(--color-warning)',
                      cursor: 'pointer'
                    }}
                  >
                    <AlertTriangle size={16} />
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default TagRulesManager;
